/**
 * Mail-template list view + entry point to the editor.
 *
 * Compact: a flat list of cards, each with name + subject + edit /
 * duplicate / delete. A "Vorlagen importieren" button up-top runs
 * `seedDefaultMailTemplates` for first-time users.
 */
import * as React from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Pencil, Plus, Sparkles, Trash2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { useToast } from '../../../components/ui/toast';
import {
  useDeleteMailTemplate,
  useMailTemplates,
  useSeedMailTemplates,
  useUpsertMailTemplate,
} from '../../../hooks/useMailTemplates';
import { makeBlock, type MailTemplate } from '@cp3/shared';

export function MailTemplatesPage() {
  const list = useMailTemplates();
  const seed = useSeedMailTemplates();
  const upsert = useUpsertMailTemplate();
  const remove = useDeleteMailTemplate();
  const { toast } = useToast();
  const items = list.data ?? [];

  async function newBlank() {
    const id = await upsert.mutateAsync({
      name: 'Neue Vorlage',
      subject: 'Betreff …',
      preheader: '',
      bgColor: '#f4f6f9',
      cardColor: '#ffffff',
      width: 600,
      rounded: true,
      category: 'transactional',
      blocks: [makeBlock('heading'), makeBlock('paragraph')],
    });
    window.location.href = `/settings/mail-templates/${id}`;
  }

  async function duplicate(t: MailTemplate) {
    const id = await upsert.mutateAsync({ ...t, id: undefined, name: `${t.name} (Kopie)` });
    toast({ title: 'Dupliziert', description: id });
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap justify-end gap-2">
        {items.length === 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              try {
                const r = await seed.mutateAsync();
                toast({
                  title: 'Vorlagen importiert',
                  description: r.skipped
                    ? 'Du hattest bereits eigene Vorlagen.'
                    : `${r.written} Default-Vorlagen angelegt.`,
                });
              } catch (err) {
                toast({
                  title: 'Import fehlgeschlagen',
                  description: (err as Error).message,
                  variant: 'destructive',
                });
              }
            }}
            disabled={seed.isPending}
          >
            {seed.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="mr-2 h-4 w-4" />
            )}
            Default-Vorlagen importieren
          </Button>
        )}
        <Button size="sm" onClick={() => void newBlank()} disabled={upsert.isPending}>
          <Plus className="mr-2 h-4 w-4" />
          Neue Vorlage
        </Button>
      </div>

      {list.isLoading ? (
        <div className="flex h-32 items-center justify-center text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Lade …
        </div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            Noch keine Mail-Vorlagen. Klick „Default-Vorlagen importieren" oder „Neue Vorlage".
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((t) => (
            <Card key={t.id} className="flex flex-col">
              <CardContent className="flex flex-1 flex-col gap-2 p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate font-medium">{t.name}</div>
                    <div className="truncate text-xs text-muted-foreground">{t.subject}</div>
                  </div>
                  <span className="rounded-full border border-border px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                    {t.blocks.length} Blöcke
                  </span>
                </div>
                <div className="mt-auto flex items-center gap-1.5 pt-3">
                  <Button asChild variant="outline" size="sm" className="flex-1">
                    <Link to={`/settings/mail-templates/${t.id}`}>
                      <Pencil className="mr-1.5 h-3.5 w-3.5" />
                      Bearbeiten
                    </Link>
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    title="Duplizieren"
                    onClick={() => void duplicate(t)}
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    title="Löschen"
                    onClick={async () => {
                      if (!window.confirm(`Vorlage "${t.name}" löschen?`)) return;
                      await remove.mutateAsync(t.id);
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
